import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { votingAPI, nomineeAPI, photoAPI, nicknameAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  fontSize: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  fontSize: 1rem;
  fontWeight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #cbd5e0;
    color: #a0aec0;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const NomineeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const NomineeCard = styled.div<{ selected: boolean }>`
  border: 2px solid ${props => props.selected ? '#667eea' : '#e5e7eb'};
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const NomineePhoto = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f3f4f6;
  margin: 0 auto 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  fontSize: 2rem;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NomineeName = styled.h4`
  text-align: center;
  margin: 0.5rem 0;
  color: #1f2937;
`;

const NomineeNickname = styled.p`
  text-align: center;
  font-style: italic;
  color: #6b7280;
  margin: 0;
  fontSize: 0.9rem;
`;

const SelectorSection = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
`;

const SelectorTitle = styled.h3`
  color: #374151;
  margin: 0 0 1rem 0;
  fontSize: 1.1rem;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const PhotoOption = styled.div<{ selected: boolean }>`
  border: 2px solid ${props => props.selected ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: #667eea;
  }
  
  img {
    width: 100%;
    height: 80px;
    object-fit: cover;
  }
  
  p {
    padding: 0.5rem;
    margin: 0;
    fontSize: 0.8rem;
    text-align: center;
    color: #6b7280;
  }
`;

const NicknameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const NicknameOption = styled.div<{ selected: boolean }>`
  border: 2px solid ${props => props.selected ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f0f4ff' : 'white'};
  text-align: center;
  
  &:hover {
    border-color: #667eea;
  }
  
  .nickname {
    fontWeight: 600;
    color: #1f2937;
    margin-bottom: 0.25rem;
  }
  
  .creator {
    fontSize: 0.8rem;
    color: #6b7280;
  }
`;

interface Category {
  id: number;
  name: string;
  description?: string;
}

interface Nominee {
  id: number;
  name: string;
  room_id: number;
  creator_name: string;
  photo_count: number;
  nickname_count: number;
  photo_filename?: string;
  nickname?: string;
}

interface Photo {
  id: number;
  filename: string;
  original_name: string;
  created_at: string;
}

interface Nickname {
  id: number;
  nickname: string;
  creator_name: string;
  created_at: string;
}

const VotingForm: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [nominees, setNominees] = useState<Nominee[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedNominee, setSelectedNominee] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Nuevos estados para selección granular
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [nicknames, setNicknames] = useState<Nickname[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [selectedNickname, setSelectedNickname] = useState('');

  const roomId = searchParams.get('room_id');

  const loadData = useCallback(async () => {
    if (!roomId) return;
    
    try {
      const [categoriesData, nomineesData] = await Promise.all([
        votingAPI.getCategories(parseInt(roomId)),
        nomineeAPI.getRoomNominees(parseInt(roomId))
      ]);
      setCategories(categoriesData);
      setNominees(nomineesData);
    } catch (error) {
      console.error('Error cargando datos:', error);
    }
  }, [roomId]);

  const loadNomineeDetails = useCallback(async (nomineeId: string) => {
    if (!nomineeId) {
      setPhotos([]);
      setNicknames([]);
      setSelectedPhoto('');
      setSelectedNickname('');
      return;
    }
    
    try {
      const [photosData, nicknamesData] = await Promise.all([
        photoAPI.getPhotosByNominee(parseInt(nomineeId)),
        nicknameAPI.getNicknamesByNominee(parseInt(nomineeId))
      ]);
      setPhotos(photosData);
      setNicknames(nicknamesData);
      setSelectedPhoto('');
      setSelectedNickname('');
    } catch (error) {
      console.error('Error cargando detalles del nominado:', error);
      setPhotos([]);
      setNicknames([]);
    }
  }, []);

  const handleNomineeChange = (nomineeId: string) => {
    setSelectedNominee(nomineeId);
    loadNomineeDetails(nomineeId);
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Verificar autenticación
  if (!user) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomId) {
      alert('Error: No se especificó la sala');
      return;
    }
    
    if (!selectedCategory || !selectedNominee) {
      alert('Por favor selecciona una categoría y un nominado');
      return;
    }
    
    setLoading(true);

    try {
      const voteData = {
        room_id: parseInt(roomId),
        category_id: parseInt(selectedCategory),
        nominee_id: parseInt(selectedNominee),
        photo_id: selectedPhoto ? parseInt(selectedPhoto) : null,
        nickname_id: selectedNickname ? parseInt(selectedNickname) : null
      };
      
      await votingAPI.vote(voteData);
      alert('¡Voto registrado exitosamente!');
      navigate(`/dashboard?room_id=${roomId}`);
    } catch (error) {
      console.error('Error al registrar voto:', error);
      alert('Error al registrar voto. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error</Title>
          <p>No se especificó la sala. Por favor regresa al dashboard.</p>
          <BackButton onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </BackButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>🗳️ Formulario de Votación</Title>
        
        <Form onSubmit={handleSubmit}>
          {/* Selector de Categoría */}
          <div>
            <label>Categoría:</label>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              required
            >
              <option value="">Selecciona una categoría</option>
              {categories.map((category: any) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Selector de Nominado */}
          <div>
            <label>Nominado:</label>
            <Select
              value={selectedNominee}
              onChange={(e) => handleNomineeChange(e.target.value)}
              required
            >
              <option value="">Selecciona un nominado</option>
              {nominees.map((nominee: any) => (
                <option key={nominee.id} value={nominee.id}>
                  {nominee.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Selector de Foto (solo si hay nominado seleccionado) */}
          {selectedNominee && (
            <SelectorSection>
              <SelectorTitle>📸 Selecciona una foto (opcional):</SelectorTitle>
              {photos.length > 0 ? (
                <PhotoGrid>
                  {photos.map((photo) => (
                    <PhotoOption
                      key={photo.id}
                      selected={selectedPhoto === photo.id.toString()}
                      onClick={() => setSelectedPhoto(photo.id.toString())}
                      style={{ width: '80px' }}
                    >
                      <img
                        src={`https://api.eventoshub.suils.es/uploads/photos/${photo.filename}`}
                        alt={photo.original_name}
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      />
                      <p>{photo.original_name}</p>
                    </PhotoOption>
                  ))}
                </PhotoGrid>
              ) : (
                <p style={{textAlign: 'center', color: '#6b7280', fontStyle: 'italic'}}>
                  No hay fotos disponibles para este nominado
                </p>
              )}
              {selectedPhoto && (
                <div style={{marginTop: '1rem', textAlign: 'center'}}>
                  <button 
                    type="button" 
                    onClick={() => setSelectedPhoto('')}
                    style={{
                      background: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      padding: '0.5rem 1rem', 
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Quitar foto seleccionada <i className="las la-times"></i>
                  </button>
                </div>
              )}
            </SelectorSection>
          )}

          {/* Selector de Apodo (solo si hay nominado seleccionado) */}
          {selectedNominee && (
            <SelectorSection>
              <SelectorTitle>🏷️ Selecciona un apodo (opcional):</SelectorTitle>
              {nicknames.length > 0 ? (
                <NicknameGrid>
                  {nicknames.map((nickname) => (
                    <NicknameOption
                      key={nickname.id}
                      selected={selectedNickname === nickname.id.toString()}
                      onClick={() => setSelectedNickname(nickname.id.toString())}
                    >
                      <div className="nickname">"{nickname.nickname}"</div>
                      <div className="creator">por {nickname.creator_name}</div>
                    </NicknameOption>
                  ))}
                </NicknameGrid>
              ) : (
                <p style={{textAlign: 'center', color: '#6b7280', fontStyle: 'italic'}}>
                  No hay apodos disponibles para este nominado
                </p>
              )}
              {selectedNickname && (
                <div style={{marginTop: '1rem', textAlign: 'center'}}>
                  <button 
                    type="button" 
                    onClick={() => setSelectedNickname('')}
                    style={{
                      background: '#ef4444', 
                      color: 'white', 
                      border: 'none', 
                      padding: '0.5rem 1rem', 
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Quitar apodo seleccionado <i className="las la-times"></i>
                  </button>
                </div>
              )}
            </SelectorSection>
          )}

          <Button 
            type="submit" 
            disabled={loading || !selectedCategory || !selectedNominee}
            style={{
              background: (!selectedCategory || !selectedNominee) ? '#d1d5db' : '#667eea',
              opacity: (!selectedCategory || !selectedNominee) ? 0.6 : 1
            }}
          >
            {loading ? 'Enviando...' : 
             (!selectedCategory || !selectedNominee) ? 'Selecciona categoría y nominado' : 
             'Enviar Voto'}
          </Button>
        </Form>
      </Card>
    </Container>
  );
};

export default VotingForm;